import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import {
  accreditedStructuresData, specialistAssignmentsData
} from "@/data/platformData";
import { doctorsData, servicesData } from "@/data/mockData";
import {
  Hospital, Plus, Pencil, Trash2, MapPin, Phone, Mail, Stethoscope, ArrowLeft
} from "lucide-react";

const empty = { name: "", region: "", province: "", address: "", phone: "", email: "", fiscalCode: "", vatNumber: "", managerId: "", specialistIds: [], handledServices: [], associatedPharmacies: [] };

const csvColumns = [
  { key: "id", label: "id" },
  { key: "name", label: "ragione_sociale" },
  { key: "region", label: "regione" },
  { key: "province", label: "provincia" },
  { key: "address", label: "indirizzo" },
  { key: "phone", label: "telefono" },
  { key: "email", label: "email" },
  { key: "fiscalCode", label: "codice_fiscale" },
  { key: "vatNumber", label: "partita_iva" }
];

export const AdminStructuresPage = () => {
  const [items, setItems] = useState(accreditedStructuresData);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [drillId, setDrillId] = useState(null);

  // Assignments matrix per structure (in-memory)
  const [assignments, setAssignments] = useState(() => {
    const m = {};
    items.forEach(s => {
      m[s.id] = {};
      doctorsData.forEach(d => {
        const a = specialistAssignmentsData.find(x => x.specialistId === d.id && x.structureId === s.id);
        m[s.id][d.id] = { enabled: !!a, services: new Set(a?.services || []) };
      });
    });
    return m;
  });

  const startEdit = (it) => { setEditing(it ? { ...it } : { ...empty, id: `str${Date.now()}` }); setOpen(true); };
  const save = () => {
    if (!editing.name) { toast.error("Inserire ragione sociale"); return; }
    setItems(prev => {
      const exists = prev.find(p => p.id === editing.id);
      return exists ? prev.map(p => p.id === editing.id ? editing : p) : [...prev, editing];
    });
    setOpen(false);
    toast.success("Struttura salvata");
  };
  const remove = (id) => { setItems(prev => prev.filter(p => p.id !== id)); toast.success("Struttura rimossa"); };

  const toggleDoc = (strId, docId) => {
    setAssignments(a => ({
      ...a,
      [strId]: { ...a[strId], [docId]: { ...a[strId][docId], enabled: !a[strId][docId].enabled } }
    }));
  };

  const toggleSrv = (strId, docId, srvId) => {
    setAssignments(a => {
      const cur = a[strId][docId];
      const next = new Set(cur.services);
      if (next.has(srvId)) next.delete(srvId); else next.add(srvId);
      return { ...a, [strId]: { ...a[strId], [docId]: { ...cur, services: next } } };
    });
  };

  const saveMatrix = (strId) => {
    const enabled = Object.values(assignments[strId]).filter(x => x.enabled).length;
    toast.success("Assegnazioni salvate", { description: `${enabled} medici attivi su questa struttura.` });
  };

  // Drill view
  if (drillId) {
    const s = items.find(x => x.id === drillId);
    return (
      <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
        <div>
          <Button variant="ghost" size="sm" className="-ml-2 mb-2" onClick={() => setDrillId(null)} data-testid="structure-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Tutte le strutture
          </Button>
          <div className="flex items-center gap-2 mb-1">
            <Stethoscope className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Associazione medico/struttura · {s.name}</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{s.name}</h1>
          <Button variant="premium" size="sm" onClick={() => saveMatrix(s.id)} className="mt-2" data-testid="structure-matrix-save-btn">Salva matrice</Button>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {doctorsData.map(d => {
            const row = assignments[s.id]?.[d.id];
            if (!row) return null;
            return (
              <Card key={d.id} className={!row.enabled ? "opacity-60" : ""} data-testid={`assign-card-${s.id}-${d.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-sm">{d.name}</CardTitle>
                      <p className="text-[11px] text-muted-foreground">{d.specialization} · {d.licenseNumber}</p>
                    </div>
                    <Switch checked={row.enabled} onCheckedChange={() => toggleDoc(s.id, d.id)} data-testid={`assign-toggle-${s.id}-${d.id}`} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-1 border-t pt-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Prestazioni abilitate</p>
                  <div className="grid grid-cols-2 gap-1">
                    {servicesData.filter(srv => srv.requiresReport).map(srv => {
                      const ck = row.services.has(srv.id);
                      return (
                        <label key={srv.id} className={`flex items-center gap-2 text-xs cursor-pointer p-1 rounded ${!row.enabled ? "pointer-events-none opacity-50" : ""}`}>
                          <input type="checkbox" checked={ck} onChange={() => toggleSrv(s.id, d.id, srv.id)} className="rounded" />
                          <span className="truncate">{srv.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Hospital className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">2 · Strutture accreditate</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Strutture accreditate</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} strutture nel network</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CsvIOButtons rows={items} columns={csvColumns} filename="strutture" onImport={(p) => setItems(p.map(x => ({ ...empty, ...x })))} testIdPrefix="structures-csv" />
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="structures-new-btn">
            <Plus className="h-4 w-4" /> Nuova struttura
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map(s => (
          <Card key={s.id} data-testid={`structure-card-${s.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{s.name}</CardTitle>
              <p className="text-xs text-muted-foreground">{s.region} · {s.province}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.address}</span>
                <div className="grid grid-cols-2 gap-x-3">
                  <span className="inline-flex items-center gap-1 truncate"><Phone className="h-3 w-3" /> {s.phone}</span>
                  <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {s.email}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                <Badge variant="secondary" className="text-[10px]">{s.specialistIds.length} medici</Badge>
                <Badge variant="secondary" className="text-[10px]">{s.handledServices.length} prestazioni</Badge>
                <Badge variant="secondary" className="text-[10px]">{s.associatedPharmacies.length} farmacie</Badge>
              </div>
              <div className="flex justify-between gap-1 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setDrillId(s.id)} className="gap-1" data-testid={`structure-drill-${s.id}`}>
                  <Stethoscope className="h-3 w-3" /> Medici associati
                </Button>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`structure-edit-${s.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(s.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && items.find(p => p.id === editing.id) ? "Modifica struttura" : "Nuova struttura"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Ragione sociale</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="structure-form-name" /></div>
              <div><Label>Regione</Label><Input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} /></div>
              <div><Label>Provincia</Label><Input value={editing.province} onChange={(e) => setEditing({ ...editing, province: e.target.value })} maxLength={2} /></div>
              <div className="col-span-2"><Label>Indirizzo</Label><Input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
              <div><Label>Telefono</Label><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
              <div><Label>Codice Fiscale</Label><Input value={editing.fiscalCode} onChange={(e) => setEditing({ ...editing, fiscalCode: e.target.value })} /></div>
              <div><Label>Partita IVA</Label><Input value={editing.vatNumber} onChange={(e) => setEditing({ ...editing, vatNumber: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="structure-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStructuresPage;
